import React, { useCallback, useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, formatApiError } from "../lib/api";
import { Fingerprint, ShieldCheck } from "@phosphor-icons/react";

export default function AccountSecurity() {
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [setup, setSetup] = useState(null); // {secret, otpauth_url}
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });
  const [passkeys, setPasskeys] = useState([]);
  const [deviceName, setDeviceName] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [me, creds] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/webauthn/credentials"),
      ]);
      setTotpEnabled(Boolean(me.data.user?.totp_enabled));
      setPasskeys(creds.data.credentials || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startTotp = async () => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const { data } = await api.post("/auth/totp/setup");
      setSetup(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/totp/confirm", { code: confirmCode });
      setBackupCodes(data.backup_codes || []);
      setSetup(null);
      setConfirmCode("");
      setTotpEnabled(true);
      setMsg("Authenticator enabled. Save your backup codes — shown once.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/totp/disable", disableForm);
      setTotpEnabled(false);
      setDisableForm({ password: "", code: "" });
      setBackupCodes(null);
      setMsg("Authenticator disabled.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const regenerateBackup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/totp/backup-codes/regenerate", disableForm);
      setBackupCodes(data.backup_codes || []);
      setDisableForm({ password: "", code: "" });
      setMsg("New backup codes generated. Old ones no longer work.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const { data } = await api.post("/auth/webauthn/register/options");
      const attestation = await startRegistration({ optionsJSON: data.options });
      await api.post("/auth/webauthn/register/complete", {
        credential: attestation,
        device_name: deviceName.trim() || "Passkey",
      });
      setDeviceName("");
      setMsg("Passkey registered.");
      await load();
    } catch (e) {
      setError(formatApiError(e) || "Passkey registration failed or cancelled");
    } finally {
      setBusy(false);
    }
  };

  const revokePasskey = async (credentialId) => {
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/webauthn/credentials/revoke", { credential_id: credentialId });
      await load();
      setMsg("Passkey removed.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-black p-4" data-testid="account-security">
      <div className="label-tag text-neutral-500 flex items-center gap-2">
        <ShieldCheck size={14} weight="bold" /> Account security
      </div>
      <p className="mt-1 text-sm text-neutral-700">
        Optional authenticator (TOTP) and passkeys. Passkey login skips the TOTP step.
      </p>

      {error && <div className="mt-3 label-tag text-red-600">{error}</div>}
      {msg && <div className="mt-3 label-tag text-green-800">{msg}</div>}

      {backupCodes && (
        <div className="mt-4 border border-black bg-[color:var(--cc-bg-2)] p-3" data-testid="backup-codes">
          <div className="label-tag">Backup codes (save these)</div>
          <ul className="mt-2 font-mono-cc text-sm grid grid-cols-2 gap-1">
            {backupCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-5 border-t border-dashed border-neutral-400 pt-4">
        <div className="label-tag">Authenticator app (TOTP)</div>
        {!totpEnabled && !setup && (
          <button type="button" disabled={busy} onClick={startTotp} className="mt-2 label-tag px-3 py-2 border border-black brutal-btn disabled:opacity-60" data-testid="totp-enable">
            Enable authenticator
          </button>
        )}
        {setup && (
          <form onSubmit={confirmTotp} className="mt-3 space-y-3">
            <p className="text-sm">Add this secret in your authenticator app, then enter a code:</p>
            <code className="block px-3 py-2 bg-[color:var(--cc-bg-2)] border border-black font-mono-cc text-sm break-all">{setup.secret}</code>
            <input
              className="w-full px-3 py-2 border border-black outline-none font-mono-cc tracking-widest"
              placeholder="6-digit code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              data-testid="totp-confirm-code"
              required
            />
            <button type="submit" disabled={busy} className="label-tag px-3 py-2 bg-black text-white brutal-btn disabled:opacity-60" data-testid="totp-confirm">
              Confirm & enable
            </button>
          </form>
        )}
        {totpEnabled && (
          <div className="mt-3 space-y-3">
            <div className="text-sm text-neutral-700">Authenticator is <strong>on</strong>.</div>
            <form onSubmit={disableTotp} className="space-y-2 max-w-sm">
              <input type="password" placeholder="Password" className="w-full px-3 py-2 border border-black outline-none" value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} required />
              <input placeholder="TOTP or backup code" className="w-full px-3 py-2 border border-black outline-none font-mono-cc" value={disableForm.code} onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value })} required />
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={busy} className="label-tag px-3 py-2 border border-black brutal-btn disabled:opacity-60" data-testid="totp-disable">Disable</button>
                <button type="button" disabled={busy} onClick={regenerateBackup} className="label-tag px-3 py-2 border border-black brutal-btn disabled:opacity-60" data-testid="totp-regen-backup">
                  Regenerate backup codes
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-dashed border-neutral-400 pt-4">
        <div className="label-tag flex items-center gap-2"><Fingerprint size={14} weight="bold" /> Passkeys</div>
        <ul className="mt-2 space-y-2">
          {passkeys.length === 0 && <li className="text-sm text-neutral-600">No passkeys yet.</li>}
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 text-sm border border-neutral-300 px-3 py-2">
              <span>{p.device_name || "Passkey"} <span className="text-neutral-500 font-mono-cc text-xs">{p.credential_id.slice(0, 12)}…</span></span>
              <button type="button" disabled={busy} onClick={() => revokePasskey(p.credential_id)} className="label-tag underline text-xs">Remove</button>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            className="px-3 py-2 border border-black outline-none flex-1 min-w-[10rem]"
            placeholder="Device name (optional)"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            data-testid="passkey-device-name"
          />
          <button type="button" disabled={busy} onClick={addPasskey} className="label-tag px-3 py-2 bg-black text-white brutal-btn disabled:opacity-60" data-testid="passkey-add">
            Add passkey
          </button>
        </div>
      </div>
    </div>
  );
}
